import React from 'react';
import { motion } from 'motion/react';

interface CoinProps {
  position: [number, number];
  cellSize: number;
  type: 'coin' | 'gem';
  onCollect?: () => void;
  isCollected?: boolean;
}

const Coin: React.FC<CoinProps> = ({ 
  position, 
  cellSize, 
  type = 'coin',
  onCollect: _onCollect, // Removed local state management - game state is source of truth
  isCollected = false 
}) => {
  // True 3D configuration matching Character3D approach
  const coinConfig = {
    coin: {
      size: cellSize * 0.45, // Slightly smaller than character for proportion
      depth: cellSize * 0.15, // 3D depth
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #FFD700 0%, #FFA000 50%, #FF8F00 100%)',
          back: 'linear-gradient(135deg, #FFA000 0%, #FF8F00 50%, #E65100 100%)',
          edge: 'linear-gradient(135deg, #FF8F00 0%, #E65100 50%, #BF360C 100%)',
        },
        effects: {
          glow: 'rgba(255, 215, 0, 0.6)',
          shadow: 'radial-gradient(ellipse, rgba(255, 193, 7, 0.4) 0%, transparent 70%)',
        }
      },
      symbol: '$',
      value: '10'
    },
    gem: {
      size: cellSize * 0.40, // Slightly smaller for gems
      depth: cellSize * 0.12,
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 50%, #4A148C 100%)',
          back: 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 50%, #2E0854 100%)',
          edge: 'linear-gradient(135deg, #4A148C 0%, #2E0854 50%, #1A0E42 100%)',
        },
        effects: {
          glow: 'rgba(156, 39, 176, 0.6)',
          shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)',
        }
      },
      symbol: '$',
      value: '50'
    }
  };

  const config = coinConfig[type];
  const elevation = cellSize * 0.08; // Floating height above ground

  // Don't render if collected - game state is the single source of truth
  if (isCollected) {
    return (
      <motion.div
        style={{
          position: 'absolute',
          left: position[0] * cellSize + cellSize / 2 - config.size / 2,
          top: position[1] * cellSize + cellSize / 2 - config.size / 2 - elevation,
          width: config.size,
          height: config.size,
          zIndex: 1500, // Above everything during collection
          perspective: '500px',
        }}
        initial={{
          scale: 1,
          opacity: 1,
          y: -elevation,
          rotateY: 0,
        }}
        animate={{
          y: [-elevation, -cellSize * 3],
          scale: [1, 1.8, 0],
          opacity: [1, 1, 0],
          rotateY: type === 'coin' ? [0, 720] : [0, 360], // Coins spin more
        }}
        exit={{
          opacity: 0,
          scale: 0,
        }}
        transition={{
          duration: 0.5,
          ease: 'easeOut',
        }}
      >
        {/* 3D Collectible with Character3D-style construction */}
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            transformStyle: 'preserve-3d',
            transformOrigin: 'center center',
          }}
        >
          {/* Front face */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transform: `translateZ(${config.depth / 2}px)`,
              background: config.colors.faces.front,
              borderRadius: type === 'coin' ? '50%' : '15%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow: `
                inset 0 3px 6px rgba(255, 255, 255, 0.4),
                inset 0 -3px 6px rgba(0, 0, 0, 0.2),
                0 0 20px ${config.colors.effects.glow}
              `,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: config.size * 0.4,
              fontWeight: 'bold',
              color: '#FFFFFF',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              clipPath: type === 'gem' 
                ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' 
                : undefined,
              backfaceVisibility: 'hidden',
            }}
          >
            +{config.value}
          </div>

          {/* Back face */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transform: `translateZ(-${config.depth / 2}px) rotateY(180deg)`,
              background: config.colors.faces.back,
              borderRadius: type === 'coin' ? '50%' : '15%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow: `
                inset 0 3px 6px rgba(255, 255, 255, 0.4),
                inset 0 -3px 6px rgba(0, 0, 0, 0.2)
              `,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: config.size * 0.35,
              fontWeight: 'bold',
              color: '#FFFFFF',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              clipPath: type === 'gem' 
                ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' 
                : undefined,
              backfaceVisibility: 'hidden',
            }}
          >
            {config.symbol}
          </div>

          {/* Edge faces for true 3D depth - only for coins (gems are faceted differently) */}
          {type === 'coin' && [...Array(8)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: '100%',
                height: `${config.depth}px`,
                top: '50%',
                left: '0%',
                transform: `
                  translateY(-50%) 
                  rotateY(${i * 45}deg) 
                  translateZ(${config.size / 2}px)
                `,
                background: config.colors.faces.edge,
                transformOrigin: 'center center',
                backfaceVisibility: 'hidden',
              }}
            />
          ))}

          {/* Gem facets for gems instead of circular edges */}
          {type === 'gem' && [
            // Top facets
            ...Array(6).map((_, i) => (
              <div
                key={`top-${i}`}
                style={{
                  position: 'absolute',
                  width: '80%',
                  height: '40%',
                  left: '10%',
                  top: '30%',
                  transform: `
                    rotateX(60deg) 
                    rotateY(${i * 60}deg) 
                    translateZ(${config.depth / 3}px)
                  `,
                  background: `linear-gradient(135deg, ${config.colors.faces.front}, ${config.colors.faces.edge})`,
                  clipPath: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)',
                  backfaceVisibility: 'hidden',
                  opacity: 0.8,
                }}
              />
            )),
            // Bottom facets
            ...Array(6).map((_, i) => (
              <div
                key={`bottom-${i}`}
                style={{
                  position: 'absolute',
                  width: '80%',
                  height: '40%',
                  left: '10%',
                  top: '30%',
                  transform: `
                    rotateX(-60deg) 
                    rotateY(${i * 60}deg) 
                    translateZ(${config.depth / 3}px)
                  `,
                  background: `linear-gradient(135deg, ${config.colors.faces.back}, ${config.colors.faces.edge})`,
                  clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 20% 100%)',
                  backfaceVisibility: 'hidden',
                  opacity: 0.8,
                }}
              />
            ))
          ]}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: position[0] * cellSize + cellSize / 2 - config.size / 2,
        top: position[1] * cellSize + cellSize / 2 - config.size / 2,
        width: config.size,
        height: config.size,
        zIndex: position[1] * 100 + position[0] + 600, // Dynamic z-index for proper layering
        perspective: '500px', // Match Character3D perspective
      }}
      initial={{ scale: 0, y: -cellSize, rotateY: 0 }}
      animate={{
        scale: 1,
        y: -elevation,
        rotateY: type === 'coin' ? 360 : 0, // Coins spin on entrance
      }}
      transition={{
        scale: {
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: (position[0] + position[1]) * 0.05,
        },
        y: {
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: (position[0] + position[1]) * 0.05,
        },
        rotateY: {
          duration: type === 'coin' ? 2 : 0,
          repeat: type === 'coin' ? Infinity : 0,
          ease: 'linear',
        },
      }}
    >
      {/* 3D Collectible Container - matching Character3D structure */}
      <motion.div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d', // Critical for true 3D
          transformOrigin: 'center center',
        }}
        animate={{
          y: [0, -8, 0], // Gentle floating animation
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: Math.random() * 2,
        }}
      >
        {/* Front face with Character3D-style construction */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transform: `translateZ(${config.depth / 2}px)`,
            background: config.colors.faces.front,
            borderRadius: type === 'coin' ? '50%' : '15%',
            border: '3px solid rgba(0, 0, 0, 0.3)',
            boxShadow: `
              inset 0 3px 6px rgba(255, 255, 255, 0.4),
              inset 0 -3px 6px rgba(0, 0, 0, 0.2),
              0 0 15px ${config.colors.effects.glow}
            `,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: config.size * (type === 'coin' ? 0.45 : 0.3),
            fontWeight: 'bold',
            color: '#FFFFFF',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            clipPath: type === 'gem' 
              ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' 
              : undefined,
            backfaceVisibility: 'hidden',
          }}
        >
          {config.symbol}
        </div>

        {/* Back face */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transform: `translateZ(-${config.depth / 2}px) rotateY(180deg)`,
            background: config.colors.faces.back,
            borderRadius: type === 'coin' ? '50%' : '15%',
            border: '3px solid rgba(0, 0, 0, 0.3)',
            boxShadow: `
              inset 0 3px 6px rgba(255, 255, 255, 0.4),
              inset 0 -3px 6px rgba(0, 0, 0, 0.2)
            `,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: config.size * 0.35,
            fontWeight: 'bold',
            color: '#FFFFFF',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            clipPath: type === 'gem' 
              ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' 
              : undefined,
            backfaceVisibility: 'hidden',
          }}
        >
          {config.value}
        </div>

        {/* Edge faces for coins (cylindrical) */}
        {type === 'coin' && [...Array(12)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '100%',
              height: `${config.depth}px`,
              top: '50%',
              left: '0%',
              transform: `
                translateY(-50%) 
                rotateY(${i * 30}deg) 
                translateZ(${config.size / 2}px)
              `,
              background: config.colors.faces.edge,
              transformOrigin: 'center center',
              backfaceVisibility: 'hidden',
            }}
          />
        ))}

        {/* Gem facets for gems instead of circular edges */}
        {type === 'gem' && [
          // Top pyramid facets
          ...Array(6).map((_, i) => (
            <div
              key={`top-${i}`}
              style={{
                position: 'absolute',
                width: '70%',
                height: '35%',
                left: '15%',
                top: '32.5%',
                transform: `
                  rotateX(45deg) 
                  rotateY(${i * 60}deg) 
                  translateZ(${config.depth / 4}px)
                `,
                background: `linear-gradient(135deg, ${config.colors.faces.front}, ${config.colors.faces.edge})`,
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 100%, 0% 100%)',
                backfaceVisibility: 'hidden',
                opacity: 0.9,
              }}
            />
          )),
          // Bottom pyramid facets
          ...Array(6).map((_, i) => (
            <div
              key={`bottom-${i}`}
              style={{
                position: 'absolute',
                width: '70%',
                height: '35%',
                left: '15%',
                top: '32.5%',
                transform: `
                  rotateX(-45deg) 
                  rotateY(${i * 60}deg) 
                  translateZ(${config.depth / 4}px)
                `,
                background: `linear-gradient(135deg, ${config.colors.faces.back}, ${config.colors.faces.edge})`,
                clipPath: 'polygon(0% 0%, 100% 0%, 75% 100%, 25% 100%)',
                backfaceVisibility: 'hidden',
                opacity: 0.9,
              }}
            />
          ))
        ]}
      </motion.div>

      {/* Sparkle effects around collectible - matching Character3D bubble approach */}
      {[...Array(4)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: '8px',
            height: '8px',
            background: config.colors.effects.glow,
            borderRadius: '50%',
            left: '46%',
            top: '46%',
            pointerEvents: 'none',
          }}
          animate={{
            x: [0, Math.cos(i * 90 * Math.PI / 180) * config.size * 1.3, 0],
            y: [0, Math.sin(i * 90 * Math.PI / 180) * config.size * 1.3, 0],
            scale: [0, 1, 0],
            opacity: [0, 0.8, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: i * 0.5 + Math.random(),
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Character3D-style shadow with perspective */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: -elevation - config.depth,
          left: '10%',
          width: '80%',
          height: '30%',
          background: config.colors.effects.shadow,
          borderRadius: '50%',
          zIndex: -1,
          transform: 'perspective(100px) rotateX(60deg)',
        }}
        animate={{
          scale: [0.8, 1.2, 0.8],
          opacity: [0.4, 0.2, 0.4],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Ambient glow effect */}
      <motion.div
        style={{
          position: 'absolute',
          width: '140%',
          height: '140%',
          left: '-20%',
          top: '-20%',
          borderRadius: type === 'coin' ? '50%' : '25%',
          background: `radial-gradient(circle, ${config.colors.effects.glow}20 0%, transparent 70%)`,
          pointerEvents: 'none',
          clipPath: type === 'gem' 
            ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' 
            : undefined,
        }}
        animate={{
          scale: [1, 1.4, 1],
          opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </motion.div>
  );
};

export default React.memo(Coin);